import { Inject, Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ConvertUnits,
  DataTransmissionMode,
  ENV_CONFIG,
  EnvironmentConfig,
  NFIdentity,
  PivotAggregates,
  ProcessStatus,
  ProcessStatusText,
  ProcessTrigger,
  TagCategory,
  UOM,
  ValueType,
  WorkflowTemplateValues,
  dcActionFlag
} from '@nibras/core/common';
import { cloneDeep, isEqual, orderBy } from 'lodash';
import moment from 'moment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { FlaggedEnum } from './flagged-enum';
import { ValueClassType, dcValueClassType } from './model';
import {
  AppQueryParams,
  AppRouteStateParams,
  DefaultAppAction,
  ProcessParams
} from './model/app-cache.model';
import { DataComponentConfig, IComponent } from './model/app.model';
import {
  AttributeOrTemplate,
  BaseAttributeOrTemplateHeader,
  IAttributeTemplateHeader,
  IdcValue,
  ParsedProcessStatus
} from './model/common.interfaces';
import { AppConstants } from './model/constants.model';
import { dcTagFlatTree } from './model/dc-tag-flat-tree.model';
import { dcAttributeDataKey } from './model/dcAttributeDataKey.model';
import {
  IdcAttributeTemplate,
  IdcAttributeTemplateGeneric,
  dcAttributeTemplate
} from './model/dcAttributeTemplate.model';
import { dcElementTemplateHeader } from './model/dcElementTemplateHeader.model';
import { dcNavigationTree } from './model/dcNavigationtree.model';
import { dcTag } from './model/dcTag.model';
import { dcTreeNode } from './model/dcTreeNode.model';
import { dcAttributeFilterTVP } from './model/dcattribute-filter-tvp.model';
import {
  dcAttribute,
  dcAttributeGeneric,
  dcAttributeHeader,
  dcAttributeHeaderExt
} from './model/dcattribute.model';
import { dcDataReference } from './model/dcdataReference.model';
import { dcElementHeader } from './model/dcelementheader.model';
import { dcQuery } from './model/dcquery.model';
import { dcQueryParameter, dcQueryParameterHeader } from './model/dcqueryParameter.model';
import { dcValue } from './model/dcvalue.model';
import { Dictionary } from './model/dictionary';
import { DuplicatedLabels } from './model/duplicate-labels.model';
import { GenericValue, KeyValueType } from './model/generic-value.model';
import { AttributeSmartValue } from './model/request/attribute-smart-value.model';
import { QueryOperation } from './model/request/request.model';
import { DataMethods } from './model/response/data-method.enum';
import { ViewProcessDetailParam } from './model/workflow/createinstance.model';
import { ProcessInstanceData } from './model/workflow/process-instance-data.model';
import { SharedCommonService } from './shared-common.service';

export interface NavigateToWorkflowAppParams<
  TAppRouteParams extends AppRouteStateParams = AppRouteStateParams
> {
  Id?: number;
  TemplateID?: number;
  RefElement?: string;
  ActivityID?: number;
  appElementVersionId?: number;
  widgetElementVersionId?: number;
  skipHistory?: boolean;
  newProcessId?: number;
  defaultAppAction?: DefaultAppAction;
  params?: Dictionary<string | number | boolean>;
  state?: TAppRouteParams;
}

@Injectable()
export class CommonService {
  constructor(
    private route: Router,
    private activatedRoute: ActivatedRoute,
    private flaggedEnum: FlaggedEnum,
    private sanitizer: DomSanitizer,
    @Inject(ENV_CONFIG) private environmentConfig: EnvironmentConfig
  ) { }

  private _rootEvents = new BehaviorSubject<RootEventArgs>(null);
  rootEvents$ = this._rootEvents.asObservable();
  public triggerViewProcessDetail: Subject<ViewProcessDetailParam> =
    new Subject<ViewProcessDetailParam>();

  public TOGGLE_LEFT_SIDEBAR_EVENT = 'toggleLeftSidebar';
  public PROCESS_TRANSITION_CLICK_EVENT = 'processTransitionClickEvent';
  public APP_SAVE_CLICK_EVENT = 'appSaveClickEvent';
  public APP_HELP_CLICK_EVENT = 'appHelpClickEvent';
  public PROCESS_CONTENT_EVENT = 'processContentEvent';
  public DATETIME_CHANGE_EVENT = 'dateTimeChangeEvent';
  public CONFIGURATION_ITEMS_CHANGE_EVENT = 'ConfigurationItemsChangeEvent';
  public DIALOG_CHANGE_EVENT = 'dialogChangeEvent';
  public APP_FEEDBACK_EVENT = 'feedbackEvent';
  public APP_SIDE_BAR_CLEAR_SEARCH = 'appSidebarClearSearchEvent';
  public APP_SIDE_BAR_FILTER_SEARCH = 'appSidebarFilterSearchEvent';

  public monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];
  public monthShortNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];

  //misc methods

  constructUrl(url: string): string {
    return this.isAbsoluteUrl(url)
      ? url
      : `${this.environmentConfig.apiBaseUrl}/${this.environmentConfig.apiBaseFolder}/${url}`;
  }

  private isAbsoluteUrl(url: string) {
    return /^https?:\/\//i.test(url);
  }

  emitRootEvent(eventArgs: RootEventArgs) {
    this._rootEvents.next(eventArgs);
  }

  getRootEvent(): Observable<RootEventArgs> {
    return this._rootEvents.asObservable();
  }

  public navigateToApp(
    appId: number,
    widgetId?: number,
    params?: Dictionary<string | number | boolean> | AppQueryParams
  ): void {
    //create the URL, push the AppID
    const navigationCommands = ['/app', appId];

    //push the widgetID to the list if available
    if (widgetId) {
      navigationCommands.push(widgetId);
    } else {
      navigationCommands.push(0);
    }

    if (params) {
      this.route.navigate(navigationCommands, { queryParams: params });
    } else {
      this.route.navigate(navigationCommands);
    }
  }
  public navigateToHome(): void {
    this.route.navigate(['/dashboard']);
  }

  public navigateToRoleAssignmentApp(tag: dcTag): void {
    const queryParams: Dictionary<string | number | boolean> = {
      object_id: tag.SourceObjectID || tag.ObjectID,
      name: tag.TagName
    };

    this.navigateToApp(AppConstants.USER_ROLE_ASSIGNMENT_APP_ID, 0, queryParams);
  }

  public reloadCurrentWorkflow(): void {
    this.route.navigate(['/app', AppConstants.STATIC_WORKFLOW_APP_ID], {
      queryParams: { forceReload: Date.now() },
      queryParamsHandling: 'merge'
    });
  }

  public navigateToWorkFlowApp<TAppRouteParams extends AppRouteStateParams = AppRouteStateParams>({
    Id = 59667,
    TemplateID = 0,
    RefElement = '',
    ActivityID = 0,
    appElementVersionId = AppConstants.STATIC_WORKFLOW_APP_ID,
    widgetElementVersionId = 0,
    skipHistory = false,
    newProcessId,
    defaultAppAction,
    params,
    state
  }: NavigateToWorkflowAppParams<TAppRouteParams> = {}): void {
    let queryParams: ProcessParams = {
      Id,
      ProcessID: TemplateID,
      RefElement,
      ActivityID,
      ...(newProcessId ? { newProcessId } : {})
    };

    const urlQueryParams = this.activatedRoute.snapshot.queryParams;
    if (urlQueryParams) {
      queryParams = { ...urlQueryParams, ...queryParams };
    }

    if (params) {
      queryParams = { ...params, ...queryParams };
    }

    if (ActivityID) {
      queryParams.ActivityID = ActivityID;
    }

    this.route.navigate(['app', appElementVersionId, widgetElementVersionId], {
      queryParams,
      skipLocationChange: skipHistory,
      state
    }); //TODO: appId needs to be updated
  }

  public navigateToAppDesigner(appName: string, params: Dictionary<string | number | boolean>) {
    this.navigateToWorkFlowApp({
      Id: -1,
      TemplateID: WorkflowTemplateValues.FormProcessConfiguration,
      RefElement: appName,
      ActivityID: 0,
      appElementVersionId: AppConstants.STATIC_WORKFLOW_APP_ID,
      widgetElementVersionId: 0,
      skipHistory: false,
      newProcessId: null,
      defaultAppAction: null,
      params
    });
  }

  public getWidgetDOMElementId(widgetId: number, processElement?: ProcessInstanceData) {
    return `#widget${widgetId}_${processElement ? processElement.ProcessInstanceID : ''}`;
  }

  public getMainAppIdFromRoute(): Observable<number> {
    return this.activatedRoute.params.pipe(
      take(1),
      map(param => param.appId)
    );
  }

  public getSupportMethodsQueryParameter(queryParameters: dcQueryParameter[]): dcQueryParameter {
    if (queryParameters && queryParameters.length > 0)
      return queryParameters.find(
        queryParameter => queryParameter.Header.AttributeTemplateName == 'SupportMethods'
      );
    return null;
  }

  public getSupportedDataMethods(value: number) {
    const methods = this.flaggedEnum.toArrayType<DataMethods>(value, DataMethods);
    return methods;
  }

  public getSupportedDataTranmissionModes(value: number) {
    const methods = this.flaggedEnum.toArrayType<DataTransmissionMode>(value, DataTransmissionMode);
    return methods;
  }

  public getSupportedDataMethodsByQueryParameters(queryParameters: dcQueryParameter[]) {
    const supportMethodsQueryParameterValue =
      this.getDataMethodQueryParameterValue(queryParameters);
    if (supportMethodsQueryParameterValue && supportMethodsQueryParameterValue > 0) {
      return this.getSupportedDataMethods(supportMethodsQueryParameterValue);
    }
    return;
  }

  public getDataMethodQueryParameterValue(queryParameters: dcQueryParameter[]) {
    const supportMethodsQueryParameter = this.getSupportMethodsQueryParameter(queryParameters);
    if (supportMethodsQueryParameter) {
      return supportMethodsQueryParameter.Header.ValueInt;
    }
    return null;
  }

  /**
   * @deprecated This method is deprecated, please use getAttributeValue. New method takes cares PrimitiveValueTypeID as well.
   */
  public getAttributeValueOld<T extends GenericValue>(
    attribute: IdcAttributeTemplateGeneric<BaseAttributeOrTemplateHeader>
  ) {
    return this.getValueFromAttribute<T>(attribute, attribute.Header.ValueTypeID);
  }

  public getAttributeValue<T extends GenericValue>(
    attribute: IdcAttributeTemplateGeneric<BaseAttributeOrTemplateHeader>
  ) {
    return this.getValueFromAttribute<T>(
      attribute,
      attribute.Header.PrimitiveValueTypeID || attribute.Header.ValueTypeID
    );
  }

  setAttributeOldValue(attribute: AttributeOrTemplate) {
    const isNoneQM =
      !attribute.Header.QueryManagerID ||
      attribute.Header.QueryManagerID === AppConstants.EMPTY_QM.ID;
    attribute.Header.oldValue =
      this.getAttributeValueOld(attribute) || isNoneQM ? attribute.Header.Value : null;
  }

  public setAttributeValue(
    attribute: IdcAttributeTemplate | dcQueryParameter,
    value: GenericValue,
    isFirstChange = false,
    setCommonValue = false,
    valueClassType?: dcValueClassType
  ) {
    let existingValue: GenericValue;
    switch (attribute.Header.ValueTypeID) {
      case ValueType.AggregateDataMethods:
      case ValueType.MetaDataLabels:
      case ValueType.UOM:
        if (!value) {
          attribute.Header.ValueString = attribute.Header.ValueInt = null;
          break;
        }
        const keyValueType = value as KeyValueType;
        attribute.Header.ValueInt = keyValueType.Key as number;
        attribute.Header.ValueString = keyValueType.Value;
        break;
      case ValueType.Double:
        existingValue = attribute.Header.ValueDouble;
        attribute.Header.ValueDouble = value as number;
        break;
      case ValueType.Int32:
      case ValueType.Int16:
      case ValueType.Int64:
        existingValue = attribute.Header.ValueInt;
        attribute.Header.ValueInt = value as number;
        break;
      case ValueType.DateTime:
        existingValue = attribute.Header.ValueTime;
        attribute.Header.ValueTime = value as Date;
        break;
      case ValueType.Boolean:
        existingValue = attribute.Header.ValueBoolean;
        attribute.Header.ValueBoolean = value as boolean;
        break;
      case ValueType.String:
        existingValue = attribute.Header.ValueString;
        attribute.Header.ValueString = value?.toString();
        break;
      default:
        this.setAttributePrimitiveValue(attribute, value, valueClassType);
        break;
    }
    if (setCommonValue) attribute.Header.Value = value;

    if (!isFirstChange) {
      this.setDirty(attribute.Header, existingValue, value);
    }
  }

  public setAttributePrimitiveValue(
    attribute: IdcAttributeTemplate | dcQueryParameter,
    value: GenericValue,
    valueClassType?: dcValueClassType
  ) {
    let existingValue: GenericValue;

    if (valueClassType?.ValueClassType === ValueClassType.Array) {
      attribute.Header.ValueString =
        typeof value === 'string' // Already converted to comma separated string
          ? value
          : Array.isArray(value)
            ? (value as GenericValue[]).join(',')
            : '';
      return;
    }

    switch (attribute.Header.PrimitiveValueTypeID) {
      case ValueType.Int32:
      case ValueType.Int16:
      case ValueType.Int64:
        existingValue = attribute.Header.ValueInt;
        attribute.Header.ValueInt = value as number;
        break;
      case ValueType.Double:
        existingValue = attribute.Header.ValueDouble;
        attribute.Header.ValueDouble = value as number;
        break;
      case ValueType.Boolean:
        existingValue = attribute.Header.ValueBoolean;
        attribute.Header.ValueBoolean = value as boolean;
        break;
      case ValueType.String:
      default:
        existingValue = attribute.Header.ValueString;
        attribute.Header.ValueString = value ? value.toString() : null;
        break;
    }
  }

  public setAttributeValueFromAttribute(
    attribute: IdcAttributeTemplate | dcQueryParameter,
    valueAttribute: IdcAttributeTemplate | dcQueryParameter,
    setCommonValue: boolean = false
  ) {
    this.setAttributeValue(
      attribute,
      this.getAttributeValueOld(valueAttribute),
      false,
      setCommonValue
    );

    if (
      valueAttribute &&
      attribute &&
      valueAttribute.Header &&
      attribute.Header &&
      attribute.Header.ValueTypeID !== attribute.Header.PrimitiveValueTypeID
    ) {
      attribute.Header.ValueString = valueAttribute.Header.ValueString;
    }
  }

  public findAndSetAttributeValue<
    T extends IdcAttributeTemplateGeneric<BaseAttributeOrTemplateHeader>
  >(attribtues: T[], name: string, value: GenericValue) {
    const attribute = this.findAttribute(attribtues, name);
    if (attribute) {
      this.setAttributeValue(attribute, value);
      return attribute;
    }
    return null;
  }

  public findAndGetAttributeValue<T extends GenericValue>(attribtues: dcAttribute[], name: string) {
    const attribute = this.findAttribute(attribtues, name);
    if (attribute) {
      return this.getAttributeValueOld<T>(attribute);
    }
  }

  private setDirty(
    attributeHeader: BaseAttributeOrTemplateHeader | dcQueryParameterHeader,
    existingValue: GenericValue,
    newValue: GenericValue
  ) {
    if (existingValue !== newValue) {
      attributeHeader.isDirty = true;
    }
  }

  public setAttributeValueByAttribute(
    originalAttribute: IAttributeTemplateHeader,
    attributeSource: IdcAttributeTemplate
  ) {
    originalAttribute.ValueDouble = attributeSource.Header.ValueDouble;
    originalAttribute.ValueInt = attributeSource.Header.ValueInt;
    originalAttribute.ValueTime = attributeSource.Header.ValueTime;
    originalAttribute.ValueBoolean = attributeSource.Header.ValueBoolean;
    originalAttribute.ValueString = attributeSource.Header.ValueString;
    return originalAttribute;
  }

  public setAttributeValueByAttributeHeader(
    originalAttribute: IAttributeTemplateHeader,
    attributeSource: dcAttributeHeader
  ) {
    originalAttribute.ValueDouble = attributeSource.ValueDouble;
    originalAttribute.ValueInt = attributeSource.ValueInt;
    originalAttribute.ValueTime = attributeSource.ValueTime;
    originalAttribute.ValueBoolean = attributeSource.ValueBoolean;
    originalAttribute.ValueString = attributeSource.ValueString;
    return originalAttribute;
  }

  public getValueFromAttribute<T extends GenericValue>(
    attribute: IdcAttributeTemplateGeneric<BaseAttributeOrTemplateHeader>,
    valueTypeID: ValueType
  ): T {
    return this.getValueFromAttributeHeader(attribute.Header, valueTypeID);
  }

  public getValueFromAttributeHeader<T extends GenericValue>(
    attributeHeader: IdcValue,
    valueTypeID: ValueType,
    primitiveValueTypeID: ValueType = valueTypeID
  ): T {
    switch (valueTypeID) {
      case ValueType.Int16:
        return attributeHeader.ValueInt as T;
      case ValueType.Int32:
        return attributeHeader.ValueInt as T;
      case ValueType.Int64:
        return attributeHeader.ValueInt as T;
      case ValueType.String:
        return attributeHeader.ValueString as T;
      case ValueType.Double:
        return attributeHeader.ValueDouble as T;
      case ValueType.DateTime:
        return attributeHeader.ValueTime as T;
      case ValueType.Boolean:
        return attributeHeader.ValueBoolean as T;
      case ValueType.AggregateDataMethods:
      case ValueType.MetaDataLabels:
      case ValueType.UOM:
        return {
          Value: attributeHeader.ValueString,
          Key: attributeHeader.ValueInt
        } as KeyValueType as T;
      default:
        return this.getValueFromPrimitiveValueType(
          attributeHeader,
          (attributeHeader as BaseAttributeOrTemplateHeader).PrimitiveValueTypeID ||
          primitiveValueTypeID
        );
    }
  }

  public getValueFromPrimitiveValueType<T extends GenericValue>(
    attributeHeader: IdcValue,
    primitiveValueTypeID: ValueType
  ): T {
    switch (primitiveValueTypeID) {
      case ValueType.Int16:
        return attributeHeader.ValueInt as T;
      case ValueType.Int32:
        return attributeHeader.ValueInt as T;
      case ValueType.Int64:
        return attributeHeader.ValueInt as T;
      case ValueType.String:
        return attributeHeader.ValueString as T;
      case ValueType.Double:
        return attributeHeader.ValueDouble as T;
      case ValueType.DateTime:
        return attributeHeader.ValueTime as T;
      case ValueType.Boolean:
        return attributeHeader.ValueBoolean as T;
      default:
        return attributeHeader.ValueString as T;
    }
  }

  public getValueFromDcValue(attribute: dcValue, valueTypeID: ValueType) {
    return SharedCommonService.getValueFromDcValue(attribute, valueTypeID);
  }

  public getSnapshotValue(selectedDataValues: dcValue[], valueTypeID: ValueType): GenericValue {
    return SharedCommonService.getSnapshotValue(selectedDataValues, valueTypeID);
  }

  public isTagExist(sourceTags: dcTag[], tag: dcTag) {
    return sourceTags && sourceTags.some(this.matchTag(tag));
  }

  public matchTag(tag: dcTag): (value: dcTag, index: number, array: dcTag[]) => boolean {
    return x => this.isTagEquals(x, tag);
  }

  public isTagEquals(sourceTag: dcTag, matchTag: dcTag): boolean {
    return (
      sourceTag.TagName == matchTag.TagName && sourceTag.ElementTypeID == matchTag.ElementTypeID
    );
  }

  public isSameCategoryTag(sourceTag: dcTag, matchTag: dcTag): boolean {
    if (!sourceTag || !matchTag) return false;
    return this.isTagEquals(sourceTag, matchTag) && sourceTag.CategoryID == matchTag.CategoryID;
  }

  public isTagExistOnFlatList(sourceTags: dcTagFlatTree[], tag: dcTag) {
    return (
      sourceTags &&
      sourceTags.some(x => x.ObjectName == tag.TagName && x.ElementTypeID == tag.ElementTypeID)
    );
  }

  public isTagFlatTreeEquals(sourceTag: dcTagFlatTree, matchTag: dcTag): boolean {
    return (
      sourceTag.ObjectName === matchTag.TagName &&
      sourceTag.ElementTypeID === matchTag.ElementTypeID
    );
  }

  getSmartAttributeFromDataKey(attributeDataKey: dcAttributeDataKey): AttributeSmartValue {
    if (attributeDataKey) {
      const smartValue = new AttributeSmartValue();
      smartValue.AttributeTemplatePath = attributeDataKey.AttributePath;
      smartValue.ElementPath = attributeDataKey.ElementPath;
      smartValue.AttributeID = attributeDataKey.AttributeID;
      smartValue.ElementVersionID = attributeDataKey.ElementVersionID;
      smartValue.RootElementVersionID = attributeDataKey.RootElementVersionID;
      smartValue.ValueTypeID = attributeDataKey.ValueTypeID;
      smartValue.ChildElementPath = attributeDataKey.ChildElementPath;
      return smartValue;
    }
  }

  public getMonthName(monthValue: number, shortMonthNameRequired = false): string {
    if (shortMonthNameRequired) return this.monthShortNames[monthValue];
    else return this.monthNames[monthValue];
  }

  public getWeekNumber(d: Date): number {
    // Copy date so don't modify original
    d = new Date(+d);
    d.setHours(0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    const weekNo = Math.ceil(((d.valueOf() - yearStart.valueOf()) / 86400000 + 1) / 7);

    return weekNo;
  }

  public parseTagsForSearchControl(data: dcTag | dcTag[]) {
    let searchTag: dcTag;
    if (data && data instanceof Array && data.length > 0) {
      searchTag = data[0];
    } else {
      searchTag = Object.assign([], data as dcTag);
    }
    return searchTag;
  }

  public getAttributeTemplateIdsOfDatMethods(dataMethod: DataMethods): dcTag {
    //TODO: need to update the list.
    switch (dataMethod) {
      case DataMethods.Snapshot:
        return AppConstants.DataFunctionTags.ELEMENT_TEMPLATE_VERSION_ID_SNAPSHOT;
      case DataMethods.RecordedValue:
        return AppConstants.DataFunctionTags.ELEMENT_TEMPLATE_VERSION_ID_RECORDEDVALUE;
      case DataMethods.RecordedValues:
        return AppConstants.DataFunctionTags.ELEMENT_TEMPLATE_VERSION_ID_RECORDEDVALUES;
      case DataMethods.RecordedValuesByCount:
        return AppConstants.DataFunctionTags.ELEMENT_TEMPLATE_VERSION_ID_RECORDEDVALUESBYCOUNT;
      case DataMethods.PlotValues:
        return AppConstants.DataFunctionTags.ELEMENT_TEMPLATE_VERSION_ID_PLOTVALUES;
      case DataMethods.InterpolatedValue:
        return AppConstants.DataFunctionTags.ELEMENT_TEMPLATE_VERSIONID_INTERPOLATED_VALUE;
      case DataMethods.InterpolatedValuesByCount:
        return AppConstants.DataFunctionTags
          .ELEMENT_TEMPLATE_VERSIONID_INTERPOLATED_VALUES_BY_COUNT;
      case DataMethods.InterpolatedValues:
        return AppConstants.DataFunctionTags.ELEMENT_TEMPLATE_VERSIONID_INTERPOLATED_VALUES;

      default:
        return AppConstants.DataFunctionTags.ELEMENT_TEMPLATE_VERSION_ID_SNAPSHOT;
    }
  }

  public filterOverdidQueryParamaters(dataReference: dcDataReference) {
    if (!dataReference) return;

    const selectedDataReference: dcDataReference = cloneDeep(dataReference);
    if (selectedDataReference.Queries && selectedDataReference.Queries.length > 0) {
      const queries: dcQuery[] = selectedDataReference.Queries;
      queries.forEach(query => {
        if (
          query.QueryParameters &&
          //Query.TemplateId is valid only in case of DxManager execution,
          //from Legacy implenations like DataExplorer TemplateId will be undefined.
          query.TemplateId != AppConstants.ElementTemplateVersionIds.FORMULA_QUERY
        ) {
          query.QueryParameters = query.QueryParameters.filter(a => a.Header.isDirty == true);
        }
      });
      selectedDataReference.Queries = queries;
    }
    return selectedDataReference;
  }

  public queryParamsChanged(queryId: number, queryParam: dcQueryParameter) {
    if (!queryParam || !queryParam.Header) {
      return;
    }

    queryParam.Header.IsOverriden = true;
    if (queryId > 0) {
      queryParam.Header.ActionFlag = queryParam.Header.AttributeQueryParameterID
        ? dcActionFlag.Update
        : dcActionFlag.Insert;
    } else {
      queryParam.Header.ActionFlag = dcActionFlag.Insert;
    }
  }

  public addGroup(key: string, value: string) {
    return { key, value };
  }

  public getFormulaDataReferenceName(attributeTemplate: dcAttributeTemplate) {
    return attributeTemplate.Header.AttributeTemplateName + '_Formula';
  }

  public setDisplayFormatForAttributeValue(
    attributeDcValue: dcValue,
    dataKey: dcAttributeDataKey,
    uom = 0,
    isTimeFormattedValue = false
  ): any {
    if (uom && isTimeFormattedValue) {
      attributeDcValue.displayValue = this.formatAttributeValueIntoDate(
        attributeDcValue,
        dataKey,
        uom
      );
    } else {
      attributeDcValue.displayValue = this.formatAttributeValue(attributeDcValue, dataKey);
    }
    return attributeDcValue.displayValue;
  }

  public formatAttributeValue(attributeDcValue: dcValue, dataKey: dcAttributeDataKey): any {
    attributeDcValue.ValueTypeID = dataKey.ValueTypeID;
    return this.formatAttributeObjectValue(attributeDcValue, dataKey.Precision);
  }

  public formatAttributeObjectValue(
    attributeDcValue: IdcValue,
    precision: number,
    ignoreDateTime = true
  ): any {
    const valueToFormat = this.parseToGetValue(attributeDcValue);

    if (Array.isArray(valueToFormat) || this.isObject(valueToFormat)) {
      if (attributeDcValue.ValueTypeID == ValueType.DoubleArray) {
        (valueToFormat as []).forEach((val, index) => {
          valueToFormat[index] = this.roundOff(val, precision);
        });
      }
      return valueToFormat;
    }

    if (!attributeDcValue.IsGood) {
      return this.getMessageFromBadDcValue(attributeDcValue);
    }

    switch (attributeDcValue.ValueTypeID) {
      case ValueType.Int16:
      case ValueType.Int32:
      case ValueType.Int64:
      case ValueType.Double:
        if (!valueToFormat || isNaN(valueToFormat)) {
          return valueToFormat;
        }
        return this.roundOff(valueToFormat, precision);
      case ValueType.DateTime:
        return ignoreDateTime ? valueToFormat : SharedCommonService.formatDate(valueToFormat);
      case ValueType.CurrentDateTime:
        return SharedCommonService.formatDate(valueToFormat);
      case ValueType.Boolean:
        if (
          attributeDcValue &&
          attributeDcValue.Value &&
          !attributeDcValue.IsGood &&
          attributeDcValue.ValueString
        ) {
          return attributeDcValue.ValueString;
        } else {
          return valueToFormat ? 'True' : 'False';
        }

      default:
        return valueToFormat;
    }
  }

  public getMessageFromBadDcValue(attributeDcValue: IdcValue): string {
    return attributeDcValue.MessageString || attributeDcValue.ValueString;
  }

  /**
   * Formats a numeric string into a compact representation, converting large numbers to a shorter format.
   * For example, `1000` becomes `"1k"`, `1000000` becomes `"1M"`, and `1000000000` becomes `"1B"`.
   * If the input is empty, null, or non-numeric, it returns an empty string or the input unchanged.
   *
   * @param value - The numeric string to format (e.g., `"1000"`, `"1000000"`).
   * @returns A formatted string (e.g., `"1k"`, `"1M"`, `"1B"`) or the original input if non-numeric.
   * @example
   * formatNumber("1500") // Returns "1.5k"
   * formatNumber("1000000") // Returns "1M"
   * formatNumber("abc") // Returns "abc"
   * formatNumber("") // Returns ""
   */
  public formatNumber(value: string): string {
    if (value == null || value.trim() === '') return '';

    const numValue = Number(value);

    if (isNaN(numValue)) return value;
    if (Math.abs(numValue) >= 1_000_000_000) return (numValue / 1_000_000_000).toFixed(1) + 'B';
    if (Math.abs(numValue) >= 1_000_000) return (numValue / 1_000_000).toFixed(1) + 'M';
    if (Math.abs(numValue) >= 1_000) return (numValue / 1_000).toFixed(1) + 'k';

    return numValue.toString();
  }

  private formatAttributeValueIntoDate(
    attributeDcValue: dcValue,
    dataKey: dcAttributeDataKey,
    uom = 0
  ) {
    let duration: any;
    switch (uom) {
      case UOM.min:
        duration = moment.duration(attributeDcValue.Value, 'minutes');
        break;
      case UOM.h:
        duration = moment.duration(attributeDcValue.Value, 'hours');
        break;
    }
    if (!duration) return;
    const obj = SharedCommonService.getDaysHoursMinutes(duration);
    return obj;
  }

  public isObject(value: any) {
    return typeof value === 'object' && value !== null;
  }

  private parseToGetValue(dataValue: IdcValue): any {
    if (dataValue.IsGood) {
      //let value = null //this.getValueFromDcValue(dataValue, dataValue.ValueTypeID);
      //if (!value) value = dataValue.Value;
      return dataValue.Value;
    }
    return dataValue.MessageString ? dataValue.MessageString : '';
  }

  public formatSentenceCase(input: string, changeCase?: boolean): string {
    if (!input) return '';
    let formatedString;
    //if (changeCase) input = input;
    if (input.startsWith('BT_')) formatedString = input.replace('BT_', '');
    else formatedString = input;
    return formatedString.replace(/_/g, ' ');
  }

  public getDataComponentIds(configData: IComponent): DataComponentConfig[] {
    if (configData) {
      return this.checkForDataSourceAttribute(configData);
    }
    return [];
  }

  private checkForDataSourceAttribute(config: IComponent, componentConfig?: IComponent) {
    const dataComponentIds: DataComponentConfig[] = [];
    if (this.hasDatasourceAttribute(config)) {
      const dataComponentConfig = this.getDataComponentConfig(config);

      if (
        componentConfig &&
        (componentConfig.Replicate || componentConfig.ReplicateByComponent > 0)
      ) {
        dataComponentConfig.ChildElementPath = componentConfig.StaticDynamicComponentIDs.includes(
          dataComponentConfig.Id
        )
          ? AppConstants.CHILD_ELEMENT_DEFAULT_PATH
          : componentConfig.ChildElementPath;
        dataComponentConfig.RootElementVersionID = componentConfig.RootElementVersionID;
      } else if (componentConfig && componentConfig.Replicate) {
        dataComponentConfig.ChildElementPath = componentConfig.StaticDynamicComponentIDs.includes(
          dataComponentConfig.Id
        )
          ? AppConstants.CHILD_ELEMENT_DEFAULT_PATH
          : componentConfig.ChildElementPath;
        dataComponentConfig.RootElementVersionID = componentConfig.RootElementVersionID;
      }

      dataComponentIds.push(dataComponentConfig);
    } else if (config.Configuration) {
      config.Configuration.forEach(configuration => {
        const _dataComponentIds = this.checkForDataSourceAttribute(
          configuration,
          config.ElementType === NFIdentity.Component
            ? config
            : componentConfig
              ? componentConfig
              : null
        );

        _dataComponentIds.forEach(dataComponentId => {
          if (config && config.Replicate) {
            dataComponentId.ChildElementPath = config.ChildElementPath;
            dataComponentId.RootElementVersionID = config.RootElementVersionID;
          }
          dataComponentIds.push(dataComponentId);
        });
      });
    }
    return dataComponentIds;
  }

  public getDataComponentConfig(config: IComponent): DataComponentConfig {
    return {
      Id: config.Id,
      ChildElementPath: config.ChildElementPath,
      RootElementVersionID: config.RootElementVersionID
    };
  }

  hasDatasourceAttribute(config: IComponent) {
    return config.hasOwnProperty(AppConstants.AttributeNames.DATA_SOURCE);
  }

  public transformToTag(node: dcTagFlatTree) {
    const nodeSelected = Object.assign({}, node);
    nodeSelected.ObjectID = node.ObjectID;
    nodeSelected.SourceObjectID = node.ObjectID;
    nodeSelected.TagName = node.ObjectName;
    nodeSelected.ElementTypeID = node.ElementTypeID;
    nodeSelected.CategoryID = node.CategoryID;
    return nodeSelected;
  }

  public isValidTag(tag: dcTag) {
    return tag.SourceObjectID || tag.ObjectID || tag.TagName || tag.ElementTypeID;
  }

  public checkTagExists(tags: dcTag[], node: dcTagFlatTree) {
    return tags && tags.length && tags.some(a => a.TagName == node.ObjectName);
  }

  tagFromNavigationTree(node: dcNavigationTree) {
    const tag = new dcTag();
    tag.TagName = node.Name;
    tag.TagID = node.TagID;
    tag.ObjectID = node.ID;
    tag.SourceObjectID = node.ID;
    tag.ElementTypeID = node.Type;
    tag.SourceClassID = NFIdentity.Element;
    tag.isDisabled = !node.IsEnabled;
    return tag;
  }

  tagFromElementTemplate(node: dcElementTemplateHeader) {
    const tag = new dcTag();
    tag.TagName = node.ElementTemplateName;
    tag.ObjectID = node.ElementTemplateID;
    tag.SourceObjectID = node.ElementTemplateID;
    tag.ElementTypeID = node.ElementTypeID;
    tag.SourceClassID = NFIdentity.ElementTemplate;
    return tag;
  }

  navigationTreeFromTag(tag: dcTag) {
    const node: dcNavigationTree = new dcNavigationTree();
    node.Name = tag.TagName;
    node.TagID = tag.TagID;
    node.ID = tag.ObjectID;
    node.ID = tag.SourceObjectID;
    node.Type = tag.ElementTypeID;
    node.IsEnabled = !tag.isDisabled;
    return node;
  }

  navigationTreeFromTreeNode(node: dcTreeNode) {
    const navigationTreeNode: dcNavigationTree = new dcNavigationTree();
    navigationTreeNode.ID = node.ID;
    navigationTreeNode.ElementTemplateVersionID = node.ElementTemplateVersionID;
    navigationTreeNode.Type = node.ElementTypeID;
    navigationTreeNode.Level = node.Level;
    navigationTreeNode.Name = node.NamingPattern || node.ElementTemplateName || node.Name;
    navigationTreeNode.ParentID = node.ParentID;
    navigationTreeNode.Elementpath = node.Elementpath;
    navigationTreeNode.children = [];
    return navigationTreeNode;
  }

  treenodeFromNavigationTree(navigationTreeNode: dcNavigationTree) {
    const node: dcTreeNode = new dcTreeNode();
    node.ElementTemplateVersionID = navigationTreeNode.ElementTemplateVersionID;
    node.ElementTypeID = navigationTreeNode.Type;
    node.Level = navigationTreeNode.Level;
    node.ElementTemplateName = navigationTreeNode.Name;
    node.ParentID = navigationTreeNode.ParentID;
    node.Elementpath = navigationTreeNode.Elementpath;

    return node;
  }

  findAttribute<T extends IdcAttributeTemplateGeneric<BaseAttributeOrTemplateHeader>>(
    attributes: T[],
    attributeName: string
  ) {
    return (
      attributes &&
      attributes.find(
        attribute =>
          attribute?.Header?.AttributeTemplateName?.toUpperCase() == attributeName?.toUpperCase()
      )
    );
  }

  findAttributeById(attributes: dcAttribute[], attributeVersionId: number) {
    return attributes.find(attribute => attribute.Header.AttributeTemplateID == attributeVersionId);
  }

  consoleLog(message: string, color: string) {
    color = color || 'black';

    switch (color) {
      case 'success':
        color = 'Green';
        break;
      case 'info':
        color = 'DodgerBlue';
        break;
      case 'error':
        color = 'Red';
        break;
      case 'warning':
        color = 'Orange';
        break;
      default:
        color = color;
    }

    console.log('%c' + message, 'color:' + color);
  }

  roundOffPrecision(valuePrecision: number): number {
    return SharedCommonService.roundOff(valuePrecision, AppConstants.DecimalPrecison);
  }

  roundOff(value: number, precision: number): number {
    return SharedCommonService.roundOff(value, precision);
  }

  getRandomNumber(max: number) {
    return Math.floor(Math.random() * Math.floor(max));
  }

  isEmptyObject(object: any) {
    return Object.getOwnPropertyNames(object).length === 0;
  }

  public sortArrayListByDate(dataList: any, dateColumnName: string): any {
    return orderBy(
      dataList,
      (row: any) => moment(row[dateColumnName]).format(AppConstants.SORT_DATE_FORMAT),
      ['desc']
    );
  }

  public sortArrayAlphabetically(
    dataList: any[],
    dateColumnName: string,
    childArrayColumnName: string
  ): any {
    if (dataList?.length) {
      dataList.sort((a, b) =>
        a[dateColumnName] < b[dateColumnName] ? -1 : a[dateColumnName] > b[dateColumnName] ? 1 : 0
      );

      dataList.forEach(item => {
        if (item && item[childArrayColumnName]) {
          this.sortArrayAlphabetically(
            item[childArrayColumnName],
            dateColumnName,
            childArrayColumnName
          );
        }
      });
    }
  }

  /** It creates copy of object using json.stringyfy. Drawback: if object contains any function it will be removed. */
  public deepCopy(object: any) {
    return JSON.parse(JSON.stringify(object));
  }

  public trimmingWhiteSpaceFromBeginningnEnd(value: string) {
    return value ? value.replace(/^\s+|\s+$/gm, '') : '';
  }

  public flattenArrayOfObject<T>(array: Array<T>, iteratorKey: string): Array<T> {
    const flat: Array<T> = [];
    if (array && array.length) {
      array.forEach((item: any) => {
        const childs = item[iteratorKey] || [];
        const itemToPush = cloneDeep(item);
        delete itemToPush[iteratorKey];
        flat.push(itemToPush);
        if (childs && childs.length) {
          const childFlatList = this.flattenArrayOfObject(childs, iteratorKey) as Array<T>;
          flat.push(...childFlatList);
        }
      });
    }
    return flat;
  }

  public flattenArrayOfObjectShallowCopy<T>(array: Array<T>, iteratorKey: string): Array<T> {
    const flat: Array<T> = [];
    if (array && array.length) {
      array.forEach((item: any) => {
        const childs = item[iteratorKey] || [];
        const itemToPush = Object.assign({}, item);
        delete itemToPush[iteratorKey];
        flat.push(itemToPush);
        if (childs && childs.length) {
          const childFlatList = this.flattenArrayOfObjectShallowCopy(
            childs,
            iteratorKey
          ) as Array<T>;
          flat.push(...childFlatList);
        }
      });
    }
    return flat;
  }

  setAttributeFilterFromDataKey(
    dataKey: dcAttributeDataKey,
    queryOperation = QueryOperation.READ,
    queryOrder = 0
  ) {
    const attribute = new dcAttributeFilterTVP();
    attribute.AttributeTemplatePath = dataKey.AttributePath;
    attribute.ElementPath = dataKey.ElementPath;
    attribute.RootElementVersionID = dataKey.RootElementVersionID;
    attribute.QueryOperation = queryOperation;
    attribute.QueryOrder = queryOrder;
    return attribute;
  }

  setAttributeQueryManagerID(attribute: AttributeOrTemplate, newQueryMangerID: number) {
    if (!attribute?.Header) {
      return;
    }
    attribute.Header.previousQueryManagerID = attribute.Header.QueryManagerID;
    attribute.Header.QueryManagerID = newQueryMangerID;
  }

  getTagFromElementHeader(header: dcElementHeader) {
    const tag = new dcTag();
    tag.TagName = header.ElementName || null;
    tag.ObjectID = header.ElementVersionID || null;
    tag.SourceObjectID = header.ElementVersionID || null;
    tag.ElementTypeID = header.ElementTypeID;
    tag.ObjectClassID = NFIdentity.Element;
    tag.CategoryID = TagCategory.ElementSelfTag;
    return tag;
  }

  isLeafNodeTag(tag: dcTag) {
    return tag.hasOwnProperty('expandable') && !(tag as dcTagFlatTree).expandable;
  }

  isFilterTag(tag: dcTag) {
    return tag.hasOwnProperty('isFilter') && (tag as dcTagFlatTree).isFilter;
  }

  isFromTree(tag: dcTag) {
    if (tag) {
      return tag.hasOwnProperty('expandable') || (tag as dcTagFlatTree).expandable;
    }
  }

  isAppFilterElementTag(tag: dcTag) {
    return (
      tag.CategoryID === TagCategory.CustomTags &&
      tag.SourceClassID === NFIdentity.Element &&
      tag.ElementTypeID !== NFIdentity.Role
    );
  }

  isAppFilterTag(tag: dcTag) {
    return (
      tag.CategoryID === TagCategory.CustomTags &&
      (tag.SourceClassID === NFIdentity.Element ||
        tag.SourceClassID === NFIdentity.ElementTemplate) &&
      tag.ElementTypeID !== NFIdentity.Role
    );
  }

  getRepeatedDataKeyItems(datakeys: dcAttributeDataKey[]) {
    const uniqueKeys: Set<string> = new Set([]);
    const repeatedItems: Set<string> = new Set([]);
    datakeys.forEach(key => {
      const uniqueKey = SharedCommonService.getUniqueDataKey(key);
      if (uniqueKeys.has(uniqueKey)) {
        repeatedItems.add(uniqueKey);
      } else uniqueKeys.add(uniqueKey);
    });
    return repeatedItems;
  }

  getDuplicatedLabelItems(components: IComponent[]) {
    const duplicatedLabels: DuplicatedLabels = { labels: [], ids: [] };
    const labels = new Set<string>();
    const duplabels: string[] = [];
    const labelIdMap: Map<string, number[]> = new Map();
    if (components && components.length) {
      components.forEach(config => {
        const val = labelIdMap.has(config.Label) ? labelIdMap.get(config.Label) : [];
        val.push(config.Id);
        labelIdMap.set(config.Label, val);
        if (!labels.has(config.Label)) {
          labels.add(config.Label);
        } else {
          duplabels.push(config.Label);
        }
      });
      duplabels.forEach(dup => {
        const ids = labelIdMap.get(dup);
        duplicatedLabels.ids.push(...ids);
        duplicatedLabels.labels.push(dup);
      });
    }
    return duplicatedLabels;
  }

  getAllEnumStringValues(enumList: Dictionary<string>) {
    const strings = [];
    for (const key in enumList) {
      if (enumList.hasOwnProperty(key) && typeof enumList[key] === 'string')
        strings.push(enumList[key]);
    }
    return strings;
  }

  getAllEnumValues<T>(enumList: Dictionary<number>) {
    const numbers: number[] = [];
    for (const key in enumList) {
      if (enumList.hasOwnProperty(key) && typeof enumList[key] === 'number')
        numbers.push(enumList[key]);
    }
    return numbers as any as T[];
  }

  getEnumTypeValue(enumList: Dictionary<number | string>, key: string) {
    return (enumList && enumList.hasOwnProperty(key) && enumList[key]) || null;
  }

  getEnumAsObject(enumList: Dictionary<string | number>, type: 'string' | 'number') {
    const returnObject: Dictionary<string | number> = {};
    for (const key in enumList) {
      if (enumList.hasOwnProperty(key) && typeof enumList[key] === type)
        returnObject[key] = enumList[key];
    }
    return returnObject;
  }

  getAttributeFromDataKey(dataKey: dcAttributeDataKey, headerName?: string) {
    const attribute = new dcAttributeGeneric<dcAttributeHeaderExt>();
    attribute.Header.ElementVersionID = dataKey.ElementVersionID;
    attribute.Header.ElementName = dataKey.ElementName;
    attribute.Header.ElementPath = dataKey.ElementPath;
    attribute.Header.AttributeTemplatePath = dataKey.AttributePath;
    attribute.Header.AttributeTemplateName = dataKey.AttributeTemplateName;
    attribute.Header.AttributeID = dataKey.AttributeID;
    attribute.Header.ValueTypeID = dataKey.ValueTypeID;
    attribute.Header.RootElementVersionID = dataKey.RootElementVersionID;
    attribute.Header.RootElementName = dataKey.RootElementName;
    attribute.Header.headerName = headerName;
    attribute.Header.UOMID = dataKey.UOMID;
    return attribute;
  }

  /**
   * Function to push value to array set
   *
   * @param _map Map of Set of string
   * @param key string key
   * @param value value to set
   */
  setToLabelMap(_map: Map<string | number, Set<string>>, key: string | number, value: string) {
    const val = _map.has(key) ? _map.get(key) : new Set([]);
    val.add(value);
    _map.set(key, val);
  }

  /**
   * Method to get agregate data based on configuration
   *
   * @param dataList array of number
   */
  getAggregatedData(dataList: number[], aggOption: PivotAggregates) {
    return SharedCommonService.getAggregatedData(dataList, aggOption);
  }

  public getStatusTextAndColor(status: ProcessStatus) {
    let cssClass = '';
    let text = '';
    switch (status) {
      case ProcessStatus.Planned:
        cssClass = AppConstants.StatusColors.INFO;
        break;
      case ProcessStatus.Pending:
        cssClass = AppConstants.StatusColors.INPROGRESS;
        break;
      case ProcessStatus.Completed:
        cssClass = AppConstants.StatusColors.SUCCESS;
        break;
      case ProcessStatus.Expired:
        cssClass = AppConstants.StatusColors.DANGER;
        break;
      case ProcessStatus.New:
        cssClass = AppConstants.StatusColors.INFO;
        break;
    }

    if (status) {
      text = ProcessStatusText[status];
    }

    return { cssClass, text } as ParsedProcessStatus;
  }

  public getChildRequestsTextAndColor(parentRow: any) {
    let cssClass = '';
    let text = '';

    if (parentRow['ExpiredChildRequest'] > 0) {
      cssClass = AppConstants.StatusColors.DANGER;
    } else if (parentRow['InProgressChildRequest'] > 0) {
      cssClass = AppConstants.StatusColors.INPROGRESS;
    } else {
      cssClass = AppConstants.StatusColors.SUCCESS;
    }

    text =
      parentRow['CompletedChildRequest'] +
      '/' +
      (parentRow['ExpiredChildRequest'] +
        parentRow['InProgressChildRequest'] +
        parentRow['CompletedChildRequest']);

    return { cssClass, text };
  }

  public getChildRequestsTooltip(parentRow: any) {
    let text = '';

    if (parentRow['ExpiredChildRequest'] > 0) {
      text = parentRow['ExpiredChildRequest'] + ' Expired';
    }
    if (parentRow['InProgressChildRequest'] > 0) {
      if (text !== '') {
        text += ' | ' + parentRow['InProgressChildRequest'] + ' In Progress';
      } else {
        text = parentRow['InProgressChildRequest'] + ' In Progress';
      }
    }
    if (parentRow['CompletedChildRequest'] > 0) {
      if (text !== '') {
        text += ' | ' + parentRow['CompletedChildRequest'] + ' Completed';
      } else {
        text = parentRow['CompletedChildRequest'] + ' Completed';
      }
    }
    return { text };
  }

  public getIconBySource(source: ProcessTrigger) {
    let icon = '';
    switch (source) {
      case ProcessTrigger.EBS:
        icon = 'trending_down';
        break;
      case ProcessTrigger.AdHoc:
        icon = 'person';
        break;
      case ProcessTrigger.Planned:
        icon = 'event_note';
        break;
      case ProcessTrigger.Link:
        icon = 'person_link';
        break;
      default:
        icon = 'error';
        break;
    }
    return icon;
  }

  isSameTag(tag: dcTag, existingTag: dcTag) {
    return isEqual(tag, existingTag);
  }

  public sanitizeHtml(html: string): string {
    const sanitizedValue = this.sanitizer.bypassSecurityTrustHtml(html);
    return this.sanitizer.sanitize(SecurityContext.HTML, sanitizedValue) || '';
  }

  public convertUnit(value: number, convertTo: ConvertUnits) {
    if (!value && isNaN(+value)) return value;
    let _value;
    const a = 1440;
    switch (convertTo) {
      case ConvertUnits.MinsToDays:
        _value = value / a;
        break;
      case ConvertUnits.DaysToMins:
        _value = value * a;
        break;
      default:
        _value = value;
        break;
    }
    return _value;
  }

  public getQueryParam<T extends AppQueryParams>(obj: T, key: keyof T): T[keyof T] {
    // Find the matching key (case insensitive)
    const matchingKey = Object.keys(obj).find(
      k => k.toLowerCase() === (key as string).toLowerCase()
    ) as keyof T;

    if (matchingKey) {
      const value = obj[matchingKey];

      // If the model's property type is 'number' and the value is a string, convert it to a number
      if (typeof obj[matchingKey] === 'string') {
        const parsedValue = Number(obj[matchingKey]);

        // const newLocal = isNaN(parsedValue) ? value : (parsedValue as T[keyof T]);
        // // Only return the value if it's a valid number
        // return newLocal;

        return isNaN(parsedValue) ? value : (parsedValue as unknown as T[keyof T]);
      }

      return value;
    }

    return;
  }
}

export class RootEventArgs {
  constructor(public eventName: string, public params: any = null) { }
}
